import { toNumber } from './dioptre.js';
export function result(findings) {
    const blocks = findings.filter((f) => f.severity === 'block');
    return {
        findings,
        ok: blocks.length === 0,
        blocks,
        warnings: findings.filter((f) => f.severity === 'warn'),
    };
}
const block = (field, rule, message, code) => code === undefined
    ? { severity: 'block', field, rule, message }
    : { severity: 'block', field, rule, message, code };
const warn = (field, rule, message) => ({
    severity: 'warn',
    field,
    rule,
    message,
});
export const DEFAULT_THRESHOLDS = {
    anisometropiaDioptres: 2.5,
    rxStalenessMonths: 24,
};
// ---------------------------------------------------------------------------------------------
// Dioptric power
// ---------------------------------------------------------------------------------------------
/** Appendix I: "Sphere | −30.00 to +30.00, 0.25 steps | B outside". */
export function validateSphere(sphere) {
    const v = toNumber(sphere);
    return v < -30 || v > 30
        ? [block('sphere', 'sphere_range', `Sphere is −30.00 to +30.00. Got ${v.toFixed(2)}.`)]
        : [];
}
/** Appendix I: "Cylinder | −10.00 to +10.00, 0.25 steps | B outside". */
export function validateCylinder(cylinder) {
    const v = toNumber(cylinder);
    return v < -10 || v > 10
        ? [block('cylinder', 'cylinder_range', `Cylinder is −10.00 to +10.00. Got ${v.toFixed(2)}.`)]
        : [];
}
/**
 * Appendix I: "Axis | 1–180 integer; required if cyl ≠ 0; rejected if cyl = 0 | B".
 *
 * Both halves block, per `decisions/0003`. `RX-004` once listed the zero-cylinder case as a warning;
 * Appendix I governs, and a stray axis on a spherical prescription is a typo rather than a choice.
 */
export function validateAxis(axis, cylinder) {
    const cyl = toNumber(cylinder);
    const findings = [];
    if (cyl !== 0 && axis === undefined) {
        findings.push(block('axis', 'axis_required', 'An axis is required when there is a cylinder.'));
    }
    if (cyl === 0 && axis !== undefined) {
        findings.push(block('axis', 'axis_rejected', 'An axis has no meaning without a cylinder. Remove it.'));
    }
    if (axis !== undefined && (!Number.isInteger(axis) || axis < 1 || axis > 180)) {
        findings.push(block('axis', 'axis_range', `Axis is a whole number from 1 to 180. Got ${axis}.`));
    }
    return findings;
}
/** Appendix I: "Add | 0.25 to +4.00 | W above 3.50". Note the warn band sits inside the range. */
export function validateAdd(add) {
    if (add === undefined)
        return [];
    const v = toNumber(add);
    if (v < 0.25 || v > 4) {
        return [block('add', 'add_range', `Add is 0.25 to +4.00. Got ${v.toFixed(2)}.`)];
    }
    return v > 3.5 ? [warn('add', 'add_high', `An add of ${v.toFixed(2)} is unusually high.`)] : [];
}
/**
 * Appendix I: "Prism | 0.25 to 10.00 **per component**, with a base direction; base required |
 * B without base, B outside 0.25–10.00".
 *
 * Per component, not resultant, per `decisions/0004`. §5.2 stores horizontal and vertical prism
 * separately and 7.00 of each is within bounds per component while being about 9.9 resultant.
 */
export function validatePrism(magnitude, base, component) {
    if (magnitude === undefined)
        return [];
    const v = toNumber(magnitude);
    const findings = [];
    if (v < 0.25 || v > 10) {
        findings.push(block(`prism_${component}`, 'prism_range', `Prism is 0.25 to 10.00. Got ${v.toFixed(2)}.`));
    }
    if (base === undefined || base === '') {
        findings.push(block(`prism_${component}`, 'prism_base_required', 'Prism needs a base direction.'));
    }
    return findings;
}
/** Appendix I: "Anisometropia | Difference between eyes above the tenant threshold | W". */
export function validateAnisometropia(odSphere, osSphere, thresholds = DEFAULT_THRESHOLDS) {
    const limit = thresholds.anisometropiaDioptres;
    if (limit === undefined)
        return [];
    const difference = Math.abs(toNumber(odSphere) - toNumber(osSphere));
    return difference > limit
        ? [
            warn('sphere', 'anisometropia', `The eyes differ by ${difference.toFixed(2)} D, above the ${limit.toFixed(2)} D threshold.`),
        ]
        : [];
}
const MEASUREMENTS = [
    {
        field: 'monocularPd',
        rule: 'monocular_pd',
        blockMin: 22,
        blockMax: 40,
        warnMin: 25,
        warnMax: 36,
        unit: 'mm',
    },
    { field: 'binocularPd', rule: 'binocular_pd', blockMin: 44, blockMax: 80, unit: 'mm' },
    {
        field: 'segmentHeight',
        rule: 'segment_height',
        blockMin: 10,
        blockMax: 35,
        warnMin: 14,
        warnMax: 30,
        unit: 'mm',
    },
    { field: 'vertexDistance', rule: 'vertex_distance', blockMin: 8, blockMax: 20, unit: 'mm' },
    { field: 'frameEyeSize', rule: 'frame_eye_size', blockMin: 38, blockMax: 70, unit: 'mm' },
    { field: 'bridge', rule: 'bridge', blockMin: 12, blockMax: 30, unit: 'mm' },
    { field: 'temple', rule: 'temple', blockMin: 110, blockMax: 160, unit: 'mm' },
];
export const MEASUREMENT_RULES = MEASUREMENTS;
export const MEASUREMENT_FIELDS = MEASUREMENTS.map((m) => m.field);
/**
 * Whether a submitted field name is one Appendix I bounds.
 *
 * The gate for anything reading field names off a request body. `validateMeasurement` takes the
 * narrowed type, so a caller iterating over user input has to come through here first and decide
 * what an unknown field means (reject the payload, or ignore it) rather than getting a throw from
 * inside the domain.
 */
export function isMeasurementField(field) {
    return MEASUREMENTS.some((m) => m.field === field);
}
/**
 * Bound one measurement. Outer bound blocks, inner band warns (`decisions/0004`).
 *
 * The field is a `MeasurementField`, not a string: passing a name Appendix I has no rule for is a
 * type error, not a runtime surprise. The throw below is the backstop for a cast or a JS caller,
 * and it is deliberately loud, because silently returning no findings for a misspelled field is a
 * measurement that reaches a lab unchecked.
 */
export function validateMeasurement(field, value) {
    if (value === undefined)
        return [];
    // Read through the widened view: the const tuple's members have different shapes, since only two
    // rows carry a warn band, and the optional fields are not reachable on the raw union.
    const spec = MEASUREMENT_RULES.find((m) => m.field === field);
    if (!spec)
        throw new Error(`No Appendix I measurement rule named ${field}`);
    if (value < spec.blockMin || value > spec.blockMax) {
        return [
            block(spec.field, spec.rule, `${spec.field} is ${spec.blockMin} to ${spec.blockMax}${spec.unit}. Got ${value}${spec.unit}.`),
        ];
    }
    if (spec.warnMin !== undefined && spec.warnMax !== undefined) {
        if (value < spec.warnMin || value > spec.warnMax) {
            return [
                warn(spec.field, `${spec.rule}_unusual`, `${value}${spec.unit} is outside the usual ${spec.warnMin} to ${spec.warnMax}${spec.unit}.`),
            ];
        }
    }
    return [];
}
// ---------------------------------------------------------------------------------------------
// What is not built yet, named so the gap is countable rather than invisible.
// ---------------------------------------------------------------------------------------------
/**
 * Appendix I rows this package cannot evaluate yet, and what each is waiting for.
 *
 * Listed rather than omitted, because a validation module that silently covers two thirds of an
 * appendix reads as complete. Several are also waiting on a threshold the spec never gave; those
 * are marked, and `decisions/0004` and `PROGRESS.md` carry the full list.
 */
export const UNIMPLEMENTED_RULES = {
    power_vs_lens_availability: 'M2: needs a chosen lens product. Blocks at order entry per CAT-011 and release gate 3.',
    corridor_vs_frame_b: 'M2: needs the lens product corridor and the frame B measurement.',
    cut_out: 'Implemented in dispense.ts evaluateChoice (CAT-015), opt-in per call. Still lacks: a spec edging allowance, a spec severity (warn vs block), and a chosen-blank field on the order screen.',
    rx_age_at_dispense: 'M1.5: needs the dispense date. Threshold exists (default 24 months).',
    rx_expiry: 'BLOCKED: RXW-004 requires a per-country, per-Rx-type default and §13 gives none for Pakistan.',
    price_band_coverage: 'M2: the pricing engine. Error code PRICE_BAND_MISSING.',
    margin_floor: 'BLOCKED: no value and no unit given (percentage of cost, of retail, or minor units).',
    discount_above_role_limit: 'M2: per-role percentage in tenant settings, no default shipped (POS-008a).',
    deposit_below_minimum: 'BLOCKED: no tenant minimum given.',
    contact_lens_brand_substitution: 'BLOCKED: §13 says nothing about brand-binding in Pakistan. The control exists in contact-lens-substitution.ts and stays inert until a pack value is supplied.',
    contact_lens_expiry: 'BLOCKED: "likely use" horizon is a function of modality and quantity, and no formula is given.',
    customer_phone_e164: 'M1.4: needs the country dialling rules.',
    duplicate_customer: 'BLOCKED: Appendix K gives the pipeline and no similarity cutoff.',
    stock_count_variance: 'M4, and BLOCKED: no tenant threshold given.',
    negative_stock: 'M4.',
    marketplace_listing: 'Out of v1 (V1-SPEC §2).',
    ai_proposed_value: 'M5, and BLOCKED: OPS-163 names a confidence threshold and gives no value.',
    back_vertex_distance_required: 'BLOCKED: §5.2 says "required for high powers" and names no power.',
    high_prism_warn: 'BLOCKED: RX-004 requires a warn for "unusually high prism" and names no value.',
    rx_type_field_consistency: 'MISSING FROM SPEC: nothing validates that a progressive Rx carries an add, or that a CL Rx carries base curve and diameter.',
};
//# sourceMappingURL=validation.js.map